import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Typography,
  Tag,
  Tabs,
  Badge,
  Button,
  theme,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";

const { Title, Text } = Typography;

const tableHeaderStyle = `
  .system-status-table .ant-table-thead > tr > th {
    white-space: nowrap;
  }
  .system-status-table .ant-table-thead > tr > th .ant-table-column-title {
    display: inline-flex;
    align-items: center;
    min-height: 22px;
  }
`;

const VPP_FCAS_LABELS = {
  0: "Contact Admin",
  1: "Never Online",
  2: "Version 1.2.6",
  3: "NO CT",
  4: "Lost Connection With Inverter",
  5: "Offline for a long time",
  6: "Can't OTA, need restart",
  7: "Need Upgrade",
  8: "Registered",
  9: "Ready For Registered",
  10: "NO CT & Lost Connection",
  11: "Offline",
  12: "Online",
};

const VPP_FCAS_COLORS = {
  0: "red",
  1: "red",
  2: "red",
  3: "orange",
  4: "orange",
  5: "orange",
  6: "gold",
  7: "gold",
  8: "blue",
  9: "green",
  10: "red",
  11: "red",
  12: "green",
};

export default function SystemStatus() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [allSystems, setAllSystems] = useState([]);
  const [filteredSystems, setFilteredSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(15);

  const roleType = localStorage.getItem("roleType") || "";
  const isSuperadmin = roleType === "SUPERADMIN";
  const userPermissions = (() => {
    try { return JSON.parse(localStorage.getItem("permissions") || "[]"); }
    catch { return []; }
  })();
  const canExport = isSuperadmin || userPermissions.includes("system:export");

  useEffect(() => {
    const fetchSystems = async () => {
      setLoading(true);
      try {
        const authToken = localStorage.getItem("token");
        const response = await fetch("/api/systems", {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        const result = await response.json();

        if (result.success) {
          // Map the data to display format
          const systems = result.data.map((s) => ({
            id: s.id,
            sn: s.sn,
            nmi: s.nmi || "-",
            group: s.control_group_name || s.view_group_name || "Unassigned",
            status: s.status || "ACTIVE",
            pvKw: s.pv_kw ? parseFloat(s.pv_kw).toFixed(1) : "-",
            inverterKw: s.inverter_kw ? parseFloat(s.inverter_kw).toFixed(1) : "-",
            batteryKwh: s.battery_kwh ? parseFloat(s.battery_kwh).toFixed(1) : "-",
            inverterName: s.inverter_name || "-",
            batteryName: s.battery_name || "-",
            controllerName: s.controller_name || "-",
            customerName: s.customer_name || "-",
            customerEmail: s.customer_email || "-",
            onboardDate: s.onboard_date || "-",
            companyName: s.company_name || "-",
            vppFcasStatus: s.vpp_fcas_status ?? null,
            // Real-time data fields - populated when real-time telemetry is available
            soc: s.soc != null ? Number(s.soc) : null,
            uptime: s.uptime || null,
          }));
          setAllSystems(systems);
          setFilteredSystems(systems);
        } else {
          message.error(result.message || "Failed to fetch systems");
        }
      } catch (error) {
        console.error("Error fetching systems:", error);
        message.error("Failed to fetch systems");
      } finally {
        setLoading(false);
      }
    };

    fetchSystems();
  }, []);

  const handleRowClick = (record) => {
    navigate(`/systems/${record.sn}`);
  };

  // Get unique groups for filters
  const uniqueGroups = [...new Set(allSystems.map((s) => s.group).filter(Boolean))];
  const groupFilters = uniqueGroups.map((g) => ({ text: g, value: g }));

  // All systems columns
  const allColumns = [
    {
      title: "Serial Number",
      dataIndex: "sn",
      key: "sn",
      render: (sn) => <Text code style={{ fontSize: 12, cursor: "pointer" }}>{sn}</Text>,
    },
    {
      title: "NMI",
      dataIndex: "nmi",
      key: "nmi",
      render: (nmi) => <Text type="secondary" style={{ fontSize: 12 }}>{nmi}</Text>,
    },
    {
      title: "Customer",
      dataIndex: "customerName",
      key: "customerName",
      render: (name) => <Text>{name}</Text>,
    },
    {
      title: "Group",
      dataIndex: "group",
      key: "group",
      filters: groupFilters,
      onFilter: (value, record) => record.group === value,
      render: (group) => <Tag color={group === "Unassigned" ? "default" : "blue"}>{group}</Tag>,
    },
    {
      title: "Control Device",
      dataIndex: "controllerName",
      key: "controllerName",
      filters: [
        { text: "Alpha ESS", value: "Alpha ESS" },
        { text: "Powow Meter", value: "Powow Meter" },
      ],
      onFilter: (value, record) => record.controllerName === value,
      render: (name) => <Text>{name}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "ACTIVE" ? "success" : status === "INACTIVE" ? "warning" : "default"}>
          {status}
        </Tag>
      ),
    },
    // VPP Status column — hidden until data pipeline is ready
    // {
    //   title: "VPP Status",
    //   dataIndex: "vppFcasStatus",
    //   key: "vppFcasStatus",
    //   filters: Object.entries(VPP_FCAS_LABELS).map(([code, label]) => ({ text: label, value: Number(code) })),
    //   onFilter: (value, record) => record.vppFcasStatus === value,
    //   render: (code) => {
    //     if (code === null || code === undefined) return <Text type="secondary">N/A</Text>;
    //     return (
    //       <Tag color={VPP_FCAS_COLORS[code] ?? "default"}>
    //         {VPP_FCAS_LABELS[code] ?? `Status ${code}`}
    //       </Tag>
    //     );
    //   },
    // },
    {
      title: "PV",
      dataIndex: "pvKw",
      key: "pvKw",
      sorter: (a, b) => parseFloat(a.pvKw || 0) - parseFloat(b.pvKw || 0),
      render: (val) => <Text>{val !== "-" ? `${val} kW` : "-"}</Text>,
    },
    {
      title: "Inverter",
      dataIndex: "inverterKw",
      key: "inverterKw",
      sorter: (a, b) => parseFloat(a.inverterKw || 0) - parseFloat(b.inverterKw || 0),
      render: (val) => <Text>{val !== "-" ? `${val} kW` : "-"}</Text>,
    },
    {
      title: "Battery",
      dataIndex: "batteryKwh",
      key: "batteryKwh",
      sorter: (a, b) => parseFloat(a.batteryKwh || 0) - parseFloat(b.batteryKwh || 0),
      render: (val) => <Text>{val !== "-" ? `${val} kWh` : "-"}</Text>,
    },
    {
      title: "Company",
      dataIndex: "companyName",
      key: "companyName",
      render: (name) => <Text type="secondary" style={{ fontSize: 12 }}>{name}</Text>,
    },
  ];

  const handleTableChange = (_pagination, _filters, _sorter, extra) => {
    setFilteredSystems(extra.currentDataSource);
  };

  const handleExportFleet = () => {
    if (filteredSystems.length === 0) {
      message.warning("No systems to export");
      return;
    }
    const headers = ["Serial Number", "NMI", "Customer", "Customer Email", "Group", "Status", "PV (kW)", "Inverter (kW)", "Battery (kWh)", "Inverter Name", "Battery Name", "Controller", "Company", "Onboard Date"];
    const rows = filteredSystems.map((s) => [
      s.sn, s.nmi, s.customerName, s.customerEmail, s.group, s.status,
      s.pvKw, s.inverterKw, s.batteryKwh,
      s.inverterName, s.batteryName, s.controllerName, s.companyName, s.onboardDate,
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `fleet_export_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    message.success(`Exported ${filteredSystems.length} systems`);
  };

  const tabItems = [
    {
      key: "all",
      label: (
        <span>
          <CheckCircleOutlined style={{ color: token.colorPrimary }} />
          All Systems
          <Badge count={allSystems.length} style={{ marginLeft: 8, backgroundColor: token.colorPrimary }} />
        </span>
      ),
      children: (
        <>
        <style>{tableHeaderStyle}</style>
        <Table
          className="system-status-table"
          columns={allColumns}
          dataSource={allSystems}
          rowKey="id"
          loading={loading}
          pagination={makePagination(pageSize, setPageSize)}
          size="middle"
          scroll={{ x: "max-content" }}
          onChange={handleTableChange}
          onRow={(record) => ({ onClick: () => handleRowClick(record), style: { cursor: "pointer" } })}
          locale={{ emptyText: "No systems found. Onboard systems to see them here." }}
        />
        </>
      ),
    },
  ];

  return (
    <PageLayout active="systemsSummary">
      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>Systems Summary</Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            View all onboarded systems. Click on a system to view details and manage offboarding.
          </Text>
        </div>
        {canExport && (
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportFleet}
            disabled={loading || allSystems.length === 0}
          >
            Export Fleet
          </Button>
        )}
      </div>

      <Card bordered={false} style={{ borderRadius: 12 }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          size="large"
        />
      </Card>
    </PageLayout>
  );
}
